import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity, ChevronDown, Plus, X } from "lucide-react";
import "./VitalsAndPhysicalExamSection.css";
import PulmonologySaveButton from "../PulmonologySaveButton/PulmonologySaveButton";
import PulmonologyMotifChips from "../PulmonologyMotifChips/PulmonologyMotifChips";

const vitals = [
  ["bloodPressure", "0/0", "mmHg"], ["pulse", "0", "bpm"], ["temperature", "0", "°C"],
  ["respiratoryRate", "0", "/min"], ["spo2Rest", "0", "%", true], ["spo2Exertion", "0", "%", true],
];
const findingKeys = ["normalMurmur", "wheezes", "crackles", "rhonchi", "decreasedSounds", "frictionRub"];
const examSystems = ["general", "cardiac", "pulmonary", "abdomen", "upperLimbs", "lowerLimbs", "headNeck", "ent", "ophthalmology", "neurological", "urinary"];
const customFindingsStorageKey = "medilink.pulmonology.custom-findings";

const readStoredArray = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = JSON.parse(window.localStorage.getItem(key) || "null");
    return Array.isArray(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
};

export default function VitalsAndPhysicalExamSection({ patientId, record, saveRecord, saving, canSave }) {
  const { t } = useTranslation("pulmonology");
  const [vitalValues, setVitalValues] = useState(() => Object.fromEntries(vitals.map(([key, value]) => [key, value])));
  const [selectedFindings, setSelectedFindings] = useState([]);
  const [storedCustomFindings] = useState(() => readStoredArray(customFindingsStorageKey, []).filter((finding) => typeof finding?.id === "string" && typeof finding?.label === "string"));
  const customFindings = storedCustomFindings.filter((finding) => selectedFindings.includes(finding.id));
  const [normalSystems, setNormalSystems] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [expandedSystems, setExpandedSystems] = useState(["general"]);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [examNotes, setExamNotes] = useState([]);
  const nextNoteId = useRef(1);
  const persistedRecord = Array.isArray(record) ? record[0] : record;

  useEffect(() => {
    if (!persistedRecord?.examinations) return;
    const examinations = persistedRecord.examinations;
    if (persistedRecord.vitalSigns) setVitalValues(persistedRecord.vitalSigns);
    setNormalSystems(examinations.filter((exam) => exam.isNormal).map((exam) => exam.name));
    setSelectedFindings([...new Set(examinations.flatMap((exam) => exam.findings || []))]);
    setSelectedZones([...new Set(examinations.flatMap((exam) => [
      ...(exam.rightZones || []).map((zone) => zone.startsWith("R") ? zone : `R${zone}`),
      ...(exam.leftZones || []).map((zone) => zone.startsWith("L") ? zone : `L${zone}`),
    ]))]);
    setExamNotes(examinations.filter((exam) => exam.notes).map((exam, index) => ({
      id: index + 1,
      systemKey: exam.name,
      zones: [...(exam.rightZones || []), ...(exam.leftZones || [])],
      sides: ["right", "left"].filter((side) => exam[`${side}Zones`]?.length),
      description: exam.notes,
    })));
  }, [persistedRecord]);

  const saveExamNote = (systemKey) => {
    const description = (noteDrafts[systemKey] || "").trim();
    if (!description) return;
    const sides = [...new Set(selectedZones.map((zone) => zone.startsWith("R") ? "right" : "left"))];
    setExamNotes((items) => [...items, {
      id: nextNoteId.current++,
      systemKey,
      zones: [...selectedZones],
      sides,
      description,
    }]);
    setNoteDrafts((items) => ({ ...items, [systemKey]: "" }));
  };

  const saveExamination = () => saveRecord?.("physical-examination", {
    vitalSigns: vitalValues,
    examinations: examSystems.map((name) => ({
      name,
      isNormal: normalSystems.includes(name),
      findings: selectedFindings,
      rightZones: selectedZones.filter((zone) => zone.startsWith("R")),
      leftZones: selectedZones.filter((zone) => zone.startsWith("L")),
      notes: examNotes.filter((note) => note.systemKey === name).map((note) => note.description).join("\n"),
    })),
  }, persistedRecord?.id);

  return (
    <div className="pulmo-form">
      <div className="pulmo-vitals-grid">
        {vitals.map(([labelKey, value, unit, warning]) => {
          const label = t(`vitals.fields.${labelKey}`);
          const lowOxygen = warning && Number(vitalValues[labelKey]) > 0 && Number(vitalValues[labelKey]) < 92;
          return <label className={`pulmo-input-field${lowOxygen ? " is-warning" : ""}`} key={labelKey}><span>{label}</span><span className="pulmo-input-wrap"><input value={vitalValues[labelKey] ?? value} aria-label={label} onChange={(event) => setVitalValues((current) => ({ ...current, [labelKey]: event.target.value }))} /><small>{unit}</small></span></label>;
        })}
      </div>
      {Number(vitalValues.spo2Rest) > 0 && Number(vitalValues.spo2Rest) < 92 && <div className="pulmo-warning"><Activity size={14} /> <span><strong>{t("common.warning")}</strong> {t("vitals.warningMessage")}</span></div>}
      <div className="pulmo-exam-head"><strong>{t("vitals.examTitle")}</strong><button type="button" className="pulmo-outline-button" onClick={() => setNormalSystems(examSystems)}>✓ {t("vitals.markNormal")}</button></div>
      <PulmonologyMotifChips
        key={`${patientId}-physical-findings`}
        patientId={patientId}
        group="physical-findings"
        defaultOptions={[...findingKeys, ...customFindings.map((finding) => finding.id)]}
        selected={selectedFindings}
        labelForOption={(option) => customFindings.find((finding) => finding.id === option)?.label || (findingKeys.includes(option) ? t(`vitals.findings.${option}`) : option)}
        onToggle={(option) => setSelectedFindings((items) => items.includes(option) ? items.filter((item) => item !== option) : [...items, option])}
        onRemove={(option) => setSelectedFindings((items) => items.filter((item) => item !== option))}
      />
      <div className="pulmo-exam-list">
        {examSystems.map((systemKey) => {
          const system = t(`vitals.systems.${systemKey}`);
          const expanded = expandedSystems.includes(systemKey);
          const isNormal = normalSystems.includes(systemKey);
          return <div className="pulmo-system" key={systemKey}>
            <div className="pulmo-system__head">
              <span className={isNormal ? "is-normal" : ""} />{system}<small>{isNormal ? `✓ ${t("common.normal")}` : ""}</small>
              <button type="button" className={`pulmo-system__toggle${expanded ? " is-expanded" : ""}`} aria-expanded={expanded} aria-controls={`pulmo-system-${systemKey}`} aria-label={t(expanded ? "page.collapse" : "page.expand", { title: system })} onClick={() => setExpandedSystems((items) => expanded ? items.filter((item) => item !== systemKey) : [...items, systemKey])}><ChevronDown size={12} /></button>
            </div>
            {expanded && <div className="pulmo-system__body" id={`pulmo-system-${systemKey}`}>
              <div className="pulmo-lung-layout">
                <div className="pulmo-lungs" aria-label={t("vitals.auscultation")}>
                  {[{ key: "right", zones: ["RUL", "RML", "RLL"] }, { key: "left", zones: ["LUL", "LLL"] }].map(({ key, zones }) => <div className="pulmo-lung" key={key}><b>{t(`vitals.sides.${key}`)}</b>{zones.map((zone) => <button type="button" key={zone} className={selectedZones.includes(zone) ? "is-selected" : ""} onClick={() => setSelectedZones((items) => items.includes(zone) ? items.filter((item) => item !== zone) : [...items, zone])}>{zone}</button>)}</div>)}
                </div>
                <div className="pulmo-note">
                  <strong>{t("vitals.zonesSelected", { count: selectedZones.length })}</strong>
                  <small>{t("vitals.zoneNote")}</small>
                  <textarea value={noteDrafts[systemKey] || ""} placeholder={t("vitals.notesPlaceholder")} onChange={(event) => setNoteDrafts((items) => ({ ...items, [systemKey]: event.target.value }))} onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      saveExamNote(systemKey);
                    }
                  }} />
                  <button type="button" className="pulmo-note__add" onClick={() => saveExamNote(systemKey)}><Plus size={12} /> {t("vitals.addNote")}</button>
                  <div className="pulmo-note__entries">
                    {examNotes.filter((note) => note.systemKey === systemKey).map((note) => <div className="pulmo-note__entry" key={note.id}>
                      <div className="pulmo-note__entry-copy">
                        <strong>{note.sides.map((side) => t(`vitals.sides.${side}`)).join(" / ")}{note.zones.length ? ` · ${note.zones.join(", ")}` : ""}</strong>
                        <span>{note.description}</span>
                      </div>
                      <button type="button" className="pulmo-note__delete" aria-label={t("vitals.deleteNote")} title={t("vitals.deleteNote")} onClick={() => setExamNotes((items) => items.filter((item) => item.id !== note.id))}><X size={14} /></button>
                    </div>)}
                  </div>
                </div>
              </div>
            </div>}
          </div>;
        })}
      </div>
      <div className="pulmo-actions">
        <PulmonologySaveButton onClick={saveExamination} saving={saving} disabled={!canSave} />
      </div>
    </div>
  );
}
